// Agenda — DIA (timeline + lista lateral), SEMANA (grade 7 dias) e MÊS (calendário).
// Timeline de altura fixa com scroll do mouse: o dia inteiro (00–24h) existe,
// e você rola para o horário que quiser (auto-scroll pro "agora" ao abrir).
// A lista lateral do dia mostra o TÍTULO COMPLETO de cada tarefa/compromisso.
// Rotinas (sono/trabalho/exercício) aparecem hachuradas atrás das sessões.
// Arrastar sessão planejada (visão dia) muda o horário. Snap de 5min.

import type { Content, RoutineEvent, Session } from '@app-types/domain';
import {
  addDaysIso,
  addMonthsIso,
  dowLabel,
  formatDateBR,
  minutesOfDayLocal,
  monthLabel,
  monthStartIso,
  toLocalDateIso,
  todayLocalIso,
} from '@core/dates';
import { formatDuration } from '@core/duration';
import { ROUTINE_KIND_META, type RoutineBlock, routineBlocksForDate } from '@core/routines';
import { sessionActions } from '@core/store';
import { RoutineModal } from '@ui/components/RoutineModal';
import { toast } from '@ui/components/Toast';
import { navigate } from '@ui/lib/router';
import { useStore } from '@ui/lib/useStore';
import { useEffect, useRef, useState } from 'preact/hooks';

const DAY_PX = 56 / 60; // px por minuto (dia) → hora = 56px
const WEEK_PX = 44 / 60; // px por minuto (semana)
const DAY_VIEWPORT_H = 620; // altura fixa do painel rolável (dia)
const WEEK_VIEWPORT_H = 620;
const SNAP = 5; // min

type View = 'day' | 'week' | 'month';

interface DaySession {
  session: Session;
  content: Content | undefined;
  startMin: number;
  durMin: number;
}

type DayItem =
  | { type: 'session'; startMin: number; endMin: number; s: DaySession }
  | { type: 'routine'; startMin: number; endMin: number; b: RoutineBlock };

function sessionColor(s: Session, isToday: boolean, nowMin: number) {
  if (s.status === 'completed')
    return { border: 'var(--green)', bg: 'rgba(0,255,157,0.10)', text: 'var(--green)' };
  if (s.status === 'abandoned')
    return { border: 'var(--red)', bg: 'rgba(255,56,96,0.08)', text: 'var(--red)' };
  if (s.status === 'active')
    return { border: 'var(--gold)', bg: 'rgba(255,215,0,0.12)', text: 'var(--gold)' };
  const late = isToday && minutesOfDayLocal(s.planned_at) + s.planned_duration_min < nowMin;
  if (late) return { border: 'var(--amber)', bg: 'rgba(255,176,0,0.09)', text: 'var(--amber)' };
  return { border: 'var(--cyan)', bg: 'rgba(0,240,255,0.09)', text: 'var(--cyan)' };
}

function fmtHM(totalMin: number): string {
  const h = Math.floor(totalMin / 60) % 24;
  const m = Math.round(totalMin % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function daySessionsOf(sessions: Session[], contents: Content[], dateIso: string): DaySession[] {
  return sessions
    .filter((s) => toLocalDateIso(s.planned_at) === dateIso)
    .map((s) => ({
      session: s,
      content: contents.find((c) => c.id === s.content_id),
      startMin: minutesOfDayLocal(s.planned_at),
      durMin: Math.max(
        5,
        s.status === 'completed' && s.actual_duration_min
          ? s.actual_duration_min
          : s.planned_duration_min,
      ),
    }))
    .sort((a, b) => a.startMin - b.startMin);
}

function routineStripe(color: string): string {
  return `repeating-linear-gradient(45deg, ${color}14 0 6px, ${color}30 6px 12px)`;
}

const STATUS_LABEL: Record<Session['status'], string> = {
  planned: 'planejada',
  active: '▶ em andamento',
  completed: '✓ feita',
  abandoned: 'abandonada',
};

export function AgendaScreen() {
  const sessions = useStore((s) => s.sessions);
  const contents = useStore((s) => s.contents);
  const routines = useStore((s) => s.routines);

  const today = todayLocalIso();
  const [view, setView] = useState<View>('day');
  const [selected, setSelected] = useState(today);
  const isToday = selected === today;

  const [routineModal, setRoutineModal] = useState<{ open: boolean; routine: RoutineEvent | null }>(
    { open: false, routine: null },
  );

  const [nowMin, setNowMin] = useState(() => minutesOfDayLocal(new Date().toISOString()));
  useEffect(() => {
    const int = setInterval(() => setNowMin(minutesOfDayLocal(new Date().toISOString())), 30000);
    return () => clearInterval(int);
  }, []);

  // scroll containers (altura fixa → rolagem) + auto-scroll pro "agora"/1º evento
  const dayScrollRef = useRef<HTMLDivElement>(null);
  const weekScrollRef = useRef<HTMLDivElement>(null);

  // ===== drag (visão dia) =====
  const dragRef = useRef<{
    id: string;
    startY: number;
    origMin: number;
    durMin: number;
    moved: boolean;
    draggable: boolean;
    daySession: DaySession;
  } | null>(null);
  const [dragState, setDragState] = useState<{ id: string; previewMin: number } | null>(null);

  function onBlockPointerDown(e: PointerEvent, d: DaySession) {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      id: d.session.id,
      startY: e.clientY,
      origMin: d.startMin,
      durMin: d.durMin,
      moved: false,
      draggable: d.session.status === 'planned',
      daySession: d,
    };
  }
  function onBlockPointerMove(e: PointerEvent) {
    const drag = dragRef.current;
    if (!drag || !drag.draggable) return;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.abs(dy) < 5) return;
    drag.moved = true;
    let newMin = drag.origMin + Math.round(dy / DAY_PX / SNAP) * SNAP;
    newMin = Math.max(0, Math.min(24 * 60 - drag.durMin, newMin));
    setDragState({ id: drag.id, previewMin: newMin });
  }
  function onBlockPointerUp() {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (!drag.moved) {
      setDragState(null);
      onSessionClick(drag.daySession);
      return;
    }
    const newMin = dragState?.previewMin ?? drag.origMin;
    setDragState(null);
    if (newMin === drag.origMin) return;
    const base = new Date(`${selected}T00:00:00`);
    const newAt = new Date(base.getTime() + newMin * 60000);
    sessionActions.reschedule(drag.id, newAt.toISOString());
    toast(`↕ Sessão movida para ${fmtHM(newMin)}`, 'ok');
  }

  function onSessionClick(d: DaySession) {
    const st = d.session.status;
    if (st === 'completed') {
      toast(`✓ Feita: ${d.content?.title || 'sessão'}`, 'ok');
      return;
    }
    if (st === 'abandoned') return;
    if (toLocalDateIso(d.session.planned_at) !== today) {
      toast('Sessão de outro dia — arraste p/ mudar o horário ou espere o dia', 'info');
      return;
    }
    navigate('pomodoro', d.session.id);
  }

  // ===== dados do dia selecionado =====
  const dayS = daySessionsOf(sessions, contents, selected);
  const dayR = routineBlocksForDate(routines, selected);

  // timeline do dia é sempre 0..24h; rolagem revela o resto
  const DAY_GRID_H = 24 * 60 * DAY_PX;
  const yOf = (min: number) => min * DAY_PX;
  const hours = Array.from({ length: 25 }, (_, h) => h);

  // lista unificada (tarefas + compromissos) ordenada por horário — texto completo
  const dayItems: DayItem[] = [
    ...dayS.map(
      (s): DayItem => ({ type: 'session', startMin: s.startMin, endMin: s.startMin + s.durMin, s }),
    ),
    ...dayR.map((b): DayItem => ({ type: 'routine', startMin: b.start_min, endMin: b.end_min, b })),
  ].sort((a, b) => a.startMin - b.startMin);

  // auto-scroll: pro "agora" (hoje) ou 1º item do dia, ao abrir/trocar de dia
  // biome-ignore lint/correctness/useExhaustiveDependencies: reposiciona ao trocar dia/visão
  useEffect(() => {
    if (view !== 'day') return;
    const el = dayScrollRef.current;
    if (!el) return;
    const anchorMin = isToday ? nowMin : (dayItems[0]?.startMin ?? 7 * 60);
    el.scrollTop = Math.max(0, yOf(anchorMin) - 90);
  }, [selected, view]);

  // ===== semana =====
  const selDow = new Date(`${selected}T12:00:00`).getDay();
  const weekStart = addDaysIso(selected, -selDow);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i));
  const weekData = weekDays.map((dIso) => ({
    dateIso: dIso,
    sessions: daySessionsOf(sessions, contents, dIso),
    blocks: routineBlocksForDate(routines, dIso),
  }));
  const WEEK_GRID_H = 24 * 60 * WEEK_PX;
  const yOfW = (min: number) => min * WEEK_PX;
  const weekHours = Array.from({ length: 25 }, (_, h) => h);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reposiciona só ao trocar semana/visão
  useEffect(() => {
    if (view !== 'week') return;
    const el = weekScrollRef.current;
    if (!el) return;
    const anchor = weekDays.includes(today) ? nowMin : 7 * 60;
    el.scrollTop = Math.max(0, yOfW(anchor) - 90);
  }, [selected, view]);

  // ===== mês =====
  const mStart = monthStartIso(selected);
  const mStartDow = new Date(`${mStart}T12:00:00`).getDay();
  const gridStart = addDaysIso(mStart, -mStartDow); // volta pro domingo
  const monthCells = Array.from({ length: 42 }, (_, i) => addDaysIso(gridStart, i));
  const curMonth = mStart.slice(0, 7);

  // resumo do dia
  const doneCount = dayS.filter((d) => d.session.status === 'completed').length;
  const totalMin = dayS.reduce((sum, d) => sum + d.durMin, 0);

  const step = view === 'week' ? 7 : 1;
  function goPrev() {
    setSelected(view === 'month' ? addMonthsIso(selected, -1) : addDaysIso(selected, -step));
  }
  function goNext() {
    setSelected(view === 'month' ? addMonthsIso(selected, 1) : addDaysIso(selected, step));
  }

  const subLabel =
    view === 'day'
      ? `${dowLabel(selected).toUpperCase()} · ${formatDateBR(selected)}${isToday ? ' · HOJE' : ''}`
      : view === 'week'
        ? `SEMANA ${formatDateBR(weekDays[0])} — ${formatDateBR(weekDays[6])}`
        : monthLabel(selected).toUpperCase();

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Agenda</h1>
          <div class="main-sub">{subLabel}</div>
        </div>
        <div class="main-actions">
          <div
            style={{
              display: 'flex',
              gap: '4px',
              padding: '3px',
              background: 'rgba(0,8,24,0.6)',
              border: '1px solid var(--border-soft)',
              borderRadius: 'var(--r-md)',
            }}
          >
            {(['day', 'week', 'month'] as const).map((v) => (
              <button
                key={v}
                type="button"
                class={`btn sm ${view === v ? 'primary' : 'ghost'}`}
                style={{ border: 'none' }}
                onClick={() => setView(v)}
              >
                {v === 'day' ? 'DIA' : v === 'week' ? 'SEMANA' : 'MÊS'}
              </button>
            ))}
          </div>
          <button class="btn sm" onClick={goPrev} title="anterior">
            ←
          </button>
          <button class={`btn sm ${isToday ? 'primary' : ''}`} onClick={() => setSelected(today)}>
            HOJE
          </button>
          <button class="btn sm" onClick={goNext} title="próximo">
            →
          </button>
          <button
            class="btn sm gold"
            onClick={() => setRoutineModal({ open: true, routine: null })}
          >
            + ROTINA
          </button>
        </div>
      </div>

      {/* Faixa da semana (navegação rápida) — dia e semana */}
      {view !== 'month' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '6px',
            marginBottom: '14px',
          }}
        >
          {weekDays.map((dIso) => {
            const cnt = sessions.filter((s) => toLocalDateIso(s.planned_at) === dIso).length;
            const isSel = dIso === selected && view === 'day';
            const isTdy = dIso === today;
            return (
              <button
                key={dIso}
                type="button"
                onClick={() => {
                  setSelected(dIso);
                  setView('day');
                }}
                style={{
                  padding: '8px 4px',
                  borderRadius: 'var(--r-md)',
                  border: `1px solid ${isSel ? 'var(--cyan)' : isTdy ? 'rgba(255,215,0,0.4)' : 'var(--border-soft)'}`,
                  background: isSel
                    ? 'linear-gradient(135deg, rgba(0,240,255,0.15), rgba(177,74,255,0.08))'
                    : 'rgba(0,8,24,0.4)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  boxShadow: isSel ? 'var(--glow-cyan)' : 'none',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '9px',
                    letterSpacing: '1.5px',
                    color: isTdy ? 'var(--gold)' : 'var(--text-dim)',
                  }}
                >
                  {dowLabel(dIso).toUpperCase()}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--f-heading)',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: isSel ? 'var(--cyan)' : 'var(--text)',
                    margin: '2px 0',
                  }}
                >
                  {formatDateBR(dIso).slice(0, 2)}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '9px',
                    color: cnt > 0 ? 'var(--purple)' : 'var(--text-mute)',
                  }}
                >
                  {cnt > 0 ? `${cnt}×` : '—'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ===================== VISÃO DIA ===================== */}
      {view === 'day' && (
        <>
          {dayS.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <span class="tag">
                {dayS.length} tarefa{dayS.length > 1 ? 's' : ''} · {formatDuration(totalMin)}
              </span>
              <span class="tag green">{doneCount} feita(s)</span>
              {dayS.length - doneCount > 0 && (
                <span class="tag amber">{dayS.length - doneCount} restante(s)</span>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* TIMELINE (rolável) */}
            <div
              class="panel"
              style={{ padding: '10px 8px', flex: '1 1 420px', minWidth: '300px' }}
            >
              {dayS.length === 0 && dayR.length === 0 ? (
                <div class="empty-state" style={{ padding: '40px 0' }}>
                  <div class="ico">▦</div>
                  <h3>Nada {isToday ? 'pra hoje' : 'neste dia'}</h3>
                  <div>
                    Gere um plano na Home ou adicione sua rotina (sono, trabalho, exercícios).
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      justifyContent: 'center',
                      marginTop: '16px',
                    }}
                  >
                    <button class="btn primary" onClick={() => navigate('home')}>
                      ◈ GERAR PLANO
                    </button>
                    <button
                      class="btn gold"
                      onClick={() => setRoutineModal({ open: true, routine: null })}
                    >
                      + ROTINA
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  ref={dayScrollRef}
                  style={{ height: `${DAY_VIEWPORT_H}px`, overflowY: 'auto', position: 'relative' }}
                >
                  <div style={{ position: 'relative', height: `${DAY_GRID_H}px` }}>
                    {hours.map((h) => (
                      <div
                        key={h}
                        style={{
                          position: 'absolute',
                          top: `${yOf(h * 60)}px`,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          gap: '10px',
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--f-mono)',
                            fontSize: '10px',
                            color: 'var(--text-mute)',
                            width: '42px',
                            textAlign: 'right',
                            transform: 'translateY(-6px)',
                          }}
                        >
                          {String(h % 24).padStart(2, '0')}:00
                        </span>
                        <div style={{ flex: 1, borderTop: '1px dashed var(--border-soft)' }} />
                      </div>
                    ))}

                    {/* ROTINAS (hachuradas, atrás) */}
                    {dayR.map((b) => {
                      const meta = ROUTINE_KIND_META[b.routine.kind];
                      const h = Math.max(18, (b.end_min - b.start_min) * DAY_PX);
                      return (
                        <button
                          key={`${b.routine.id}-${b.spill ? 'sp' : 'm'}`}
                          type="button"
                          onClick={() => setRoutineModal({ open: true, routine: b.routine })}
                          title={`${meta.icon} ${b.routine.title} · ${fmtHM(b.start_min)}–${fmtHM(b.end_min)} · clique p/ editar`}
                          style={{
                            position: 'absolute',
                            top: `${yOf(b.start_min)}px`,
                            left: '58px',
                            right: '8px',
                            height: `${h}px`,
                            borderRadius: 'var(--r-sm)',
                            border: `1px dashed ${meta.color}66`,
                            background: routineStripe(meta.color),
                            cursor: 'pointer',
                            textAlign: 'left',
                            padding: '3px 10px',
                            overflow: 'hidden',
                            zIndex: 1,
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--f-mono)',
                              fontSize: '10px',
                              color: meta.color,
                              fontWeight: 700,
                            }}
                          >
                            {meta.icon} {b.routine.title}
                            {h > 30 && ` · ${fmtHM(b.start_min)}–${fmtHM(b.end_min)}`}
                            {b.spill && ' (continuação)'}
                          </span>
                        </button>
                      );
                    })}

                    {/* AGORA */}
                    {isToday && (
                      <div
                        style={{
                          position: 'absolute',
                          top: `${yOf(nowMin)}px`,
                          left: '52px',
                          right: 0,
                          height: '2px',
                          background: 'var(--pink)',
                          boxShadow: '0 0 8px var(--pink)',
                          zIndex: 5,
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            left: '-46px',
                            top: '-7px',
                            fontFamily: 'var(--f-mono)',
                            fontSize: '9px',
                            color: 'var(--pink)',
                            fontWeight: 700,
                          }}
                        >
                          {fmtHM(nowMin)}
                        </span>
                      </div>
                    )}

                    {/* SESSÕES (arrastáveis quando planejadas) */}
                    {dayS.map((d) => {
                      const col = sessionColor(d.session, isToday, nowMin);
                      const isDragging = dragState?.id === d.session.id;
                      const topMin = isDragging ? dragState.previewMin : d.startMin;
                      const height = Math.max(26, d.durMin * DAY_PX);
                      const draggable = d.session.status === 'planned';
                      return (
                        <button
                          key={d.session.id}
                          type="button"
                          onPointerDown={(e) => onBlockPointerDown(e as unknown as PointerEvent, d)}
                          onPointerMove={(e) => onBlockPointerMove(e as unknown as PointerEvent)}
                          onPointerUp={onBlockPointerUp}
                          title={`${d.content?.title || '—'} · ${fmtHM(topMin)}–${fmtHM(topMin + d.durMin)}${draggable ? ' · arraste p/ mover' : ''}`}
                          style={{
                            position: 'absolute',
                            top: `${yOf(topMin)}px`,
                            left: '58px',
                            right: '8px',
                            height: `${height}px`,
                            borderRadius: 'var(--r-sm)',
                            border: `1px solid ${col.border}`,
                            borderLeft: `4px solid ${col.border}`,
                            background: isDragging ? 'rgba(0,240,255,0.18)' : col.bg,
                            cursor: draggable ? 'grab' : 'pointer',
                            textAlign: 'left',
                            padding: '4px 10px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: height > 40 ? 'flex-start' : 'center',
                            zIndex: isDragging ? 10 : 2,
                            touchAction: 'none',
                            boxShadow: isDragging ? 'var(--glow-cyan)' : 'none',
                            animation:
                              d.session.status === 'active'
                                ? 'pulse 1.6s ease-in-out infinite'
                                : 'none',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: '8px',
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'var(--f-mono)',
                                fontSize: '10px',
                                color: col.text,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {fmtHM(topMin)}
                            </span>
                            <span
                              style={{
                                fontSize: '13px',
                                color:
                                  d.session.status === 'completed'
                                    ? 'var(--text-dim)'
                                    : 'var(--text)',
                                fontWeight: 600,
                                lineHeight: 1.25,
                                overflow: 'hidden',
                                textDecoration:
                                  d.session.status === 'completed' ? 'line-through' : 'none',
                              }}
                            >
                              {d.content?.title || '—'}
                            </span>
                          </div>
                          {height > 42 && (
                            <div
                              style={{
                                fontFamily: 'var(--f-mono)',
                                fontSize: '9.5px',
                                color: 'var(--text-dim)',
                                marginTop: '2px',
                              }}
                            >
                              {formatDuration(d.durMin)}
                              {d.session.status === 'completed' && ' · ✓ feita'}
                              {d.session.status === 'active' && ' · ▶ em andamento'}
                              {d.session.status === 'planned' && ' · arraste ↕ ou clique'}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* LISTA DO DIA — títulos completos (tarefas + compromissos) */}
            {dayItems.length > 0 && (
              <div
                class="panel"
                style={{ flex: '1 1 280px', minWidth: '260px', maxWidth: '420px' }}
              >
                <div class="panel-title">
                  ▤ <b>TAREFAS &amp; COMPROMISSOS</b>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {dayItems.map((it) => {
                    if (it.type === 'routine') {
                      const meta = ROUTINE_KIND_META[it.b.routine.kind];
                      return (
                        <button
                          key={`r-${it.b.routine.id}-${it.b.spill ? 'sp' : 'm'}`}
                          type="button"
                          onClick={() => setRoutineModal({ open: true, routine: it.b.routine })}
                          style={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'flex-start',
                            textAlign: 'left',
                            padding: '9px 11px',
                            borderRadius: 'var(--r-sm)',
                            border: '1px solid var(--border-soft)',
                            borderLeft: `4px dashed ${meta.color}`,
                            background: 'rgba(0,8,24,0.4)',
                            cursor: 'pointer',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--f-mono)',
                              fontSize: '10px',
                              color: meta.color,
                              fontWeight: 700,
                              flexShrink: 0,
                              width: '78px',
                            }}
                          >
                            {fmtHM(it.startMin)}–{fmtHM(it.endMin)}
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span
                              style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 600 }}
                            >
                              {meta.icon} {it.b.routine.title}
                              {it.b.spill && ' (continuação)'}
                            </span>
                            <span
                              style={{
                                display: 'block',
                                fontFamily: 'var(--f-mono)',
                                fontSize: '10px',
                                color: 'var(--text-dim)',
                                marginTop: '2px',
                              }}
                            >
                              {meta.label}
                            </span>
                          </span>
                        </button>
                      );
                    }
                    const d = it.s;
                    const col = sessionColor(d.session, isToday, nowMin);
                    return (
                      <button
                        key={`s-${d.session.id}`}
                        type="button"
                        onClick={() => onSessionClick(d)}
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          padding: '9px 11px',
                          borderRadius: 'var(--r-sm)',
                          border: '1px solid var(--border-soft)',
                          borderLeft: `4px solid ${col.border}`,
                          background: 'rgba(0,8,24,0.4)',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--f-mono)',
                            fontSize: '10px',
                            color: col.text,
                            fontWeight: 700,
                            flexShrink: 0,
                            width: '78px',
                          }}
                        >
                          {fmtHM(it.startMin)}–{fmtHM(it.endMin)}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: '13px',
                              color:
                                d.session.status === 'completed'
                                  ? 'var(--text-dim)'
                                  : 'var(--text)',
                              fontWeight: 600,
                              lineHeight: 1.3,
                              textDecoration:
                                d.session.status === 'completed' ? 'line-through' : 'none',
                            }}
                          >
                            {d.content?.title || '—'}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              fontFamily: 'var(--f-mono)',
                              fontSize: '10px',
                              color: col.text,
                              marginTop: '2px',
                            }}
                          >
                            {formatDuration(d.durMin)} · {STATUS_LABEL[d.session.status]}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===================== VISÃO SEMANA ===================== */}
      {view === 'week' && (
        <div class="panel" style={{ padding: '14px 10px' }}>
          {/* header dos dias (fixo acima da grade rolável) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '44px repeat(7, 1fr)',
              gap: '4px',
              minWidth: '760px',
              marginBottom: '4px',
            }}
          >
            <div />
            {weekData.map((wd) => {
              const isTdy = wd.dateIso === today;
              return (
                <button
                  key={wd.dateIso}
                  type="button"
                  onClick={() => {
                    setSelected(wd.dateIso);
                    setView('day');
                  }}
                  style={{
                    padding: '6px 2px',
                    borderRadius: 'var(--r-sm)',
                    border: `1px solid ${isTdy ? 'var(--gold)' : 'var(--border-soft)'}`,
                    background: isTdy ? 'rgba(255,215,0,0.07)' : 'rgba(0,8,24,0.4)',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                  title="clique p/ abrir o dia"
                >
                  <div
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '9px',
                      letterSpacing: '1px',
                      color: isTdy ? 'var(--gold)' : 'var(--text-dim)',
                    }}
                  >
                    {dowLabel(wd.dateIso).toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--f-heading)',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--text)',
                    }}
                  >
                    {formatDateBR(wd.dateIso)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* grade rolável */}
          <div ref={weekScrollRef} style={{ height: `${WEEK_VIEWPORT_H}px`, overflowY: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '44px repeat(7, 1fr)',
                gap: '4px',
                minWidth: '760px',
              }}
            >
              {/* coluna de horas */}
              <div style={{ position: 'relative', height: `${WEEK_GRID_H}px` }}>
                {weekHours.map((h) => (
                  <span
                    key={h}
                    style={{
                      position: 'absolute',
                      top: `${yOfW(h * 60) - 6}px`,
                      right: '4px',
                      fontFamily: 'var(--f-mono)',
                      fontSize: '9px',
                      color: 'var(--text-mute)',
                    }}
                  >
                    {String(h % 24).padStart(2, '0')}h
                  </span>
                ))}
              </div>
              {weekData.map((wd) => {
                const isTdy = wd.dateIso === today;
                return (
                  <div
                    key={wd.dateIso}
                    style={{
                      position: 'relative',
                      height: `${WEEK_GRID_H}px`,
                      background: 'rgba(0,8,24,0.35)',
                      borderRadius: 'var(--r-sm)',
                      border: `1px solid ${isTdy ? 'rgba(255,215,0,0.25)' : 'var(--border-soft)'}`,
                    }}
                  >
                    {weekHours.map((h) => (
                      <div
                        key={h}
                        style={{
                          position: 'absolute',
                          top: `${yOfW(h * 60)}px`,
                          left: 0,
                          right: 0,
                          borderTop: '1px dashed rgba(0,240,255,0.05)',
                          pointerEvents: 'none',
                        }}
                      />
                    ))}

                    {wd.blocks.map((b) => {
                      const meta = ROUTINE_KIND_META[b.routine.kind];
                      return (
                        <button
                          key={`${b.routine.id}-${b.spill ? 'sp' : 'm'}`}
                          type="button"
                          onClick={() => setRoutineModal({ open: true, routine: b.routine })}
                          title={`${meta.icon} ${b.routine.title} · ${fmtHM(b.start_min)}–${fmtHM(b.end_min)}`}
                          style={{
                            position: 'absolute',
                            top: `${yOfW(b.start_min)}px`,
                            left: '2px',
                            right: '2px',
                            height: `${Math.max(10, (b.end_min - b.start_min) * WEEK_PX)}px`,
                            borderRadius: '3px',
                            border: `1px dashed ${meta.color}55`,
                            background: routineStripe(meta.color),
                            cursor: 'pointer',
                            padding: '0 3px',
                            overflow: 'hidden',
                            zIndex: 1,
                          }}
                        >
                          {(b.end_min - b.start_min) * WEEK_PX > 16 && (
                            <span
                              style={{
                                fontFamily: 'var(--f-mono)',
                                fontSize: '8.5px',
                                color: meta.color,
                                fontWeight: 700,
                              }}
                            >
                              {meta.icon}
                            </span>
                          )}
                        </button>
                      );
                    })}

                    {wd.sessions.map((d) => {
                      const col = sessionColor(d.session, isTdy, nowMin);
                      const h = Math.max(14, d.durMin * WEEK_PX);
                      return (
                        <button
                          key={d.session.id}
                          type="button"
                          onClick={() => {
                            if (
                              isTdy &&
                              (d.session.status === 'planned' || d.session.status === 'active')
                            ) {
                              navigate('pomodoro', d.session.id);
                            } else {
                              setSelected(wd.dateIso);
                              setView('day');
                            }
                          }}
                          title={`${d.content?.title || '—'} · ${fmtHM(d.startMin)}–${fmtHM(d.startMin + d.durMin)}`}
                          style={{
                            position: 'absolute',
                            top: `${yOfW(d.startMin)}px`,
                            left: '3px',
                            right: '3px',
                            height: `${h}px`,
                            borderRadius: '3px',
                            border: `1px solid ${col.border}`,
                            borderLeft: `3px solid ${col.border}`,
                            background: col.bg,
                            cursor: 'pointer',
                            textAlign: 'left',
                            padding: '2px 5px',
                            overflow: 'hidden',
                            zIndex: 2,
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--f-mono)',
                              fontSize: '8.5px',
                              color: col.text,
                              fontWeight: 700,
                            }}
                          >
                            {fmtHM(d.startMin)}
                          </span>
                          {h > 26 && (
                            <span
                              style={{
                                display: 'block',
                                fontSize: '10px',
                                color:
                                  d.session.status === 'completed'
                                    ? 'var(--text-dim)'
                                    : 'var(--text)',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textDecoration:
                                  d.session.status === 'completed' ? 'line-through' : 'none',
                              }}
                            >
                              {d.content?.title || '—'}
                            </span>
                          )}
                        </button>
                      );
                    })}

                    {isTdy && (
                      <div
                        style={{
                          position: 'absolute',
                          top: `${yOfW(nowMin)}px`,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'var(--pink)',
                          boxShadow: '0 0 6px var(--pink)',
                          zIndex: 5,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===================== VISÃO MÊS ===================== */}
      {view === 'month' && (
        <div class="panel" style={{ padding: '14px 12px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px',
              marginBottom: '6px',
            }}
          >
            {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map((d) => (
              <div
                key={d}
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '9px',
                  letterSpacing: '1px',
                  color: 'var(--text-dim)',
                  textAlign: 'center',
                  padding: '2px 0',
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridAutoRows: '1fr',
              gap: '4px',
            }}
          >
            {monthCells.map((dIso) => {
              const inMonth = dIso.slice(0, 7) === curMonth;
              const isTdy = dIso === today;
              const daySessions = daySessionsOf(sessions, contents, dIso);
              const done = daySessions.filter((d) => d.session.status === 'completed').length;
              const rCount = routineBlocksForDate(routines, dIso).length;
              return (
                <button
                  key={dIso}
                  type="button"
                  onClick={() => {
                    setSelected(dIso);
                    setView('day');
                  }}
                  title={`${formatDateBR(dIso)} · ${daySessions.length} tarefa(s)`}
                  style={{
                    minHeight: '96px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    textAlign: 'left',
                    padding: '6px 7px',
                    borderRadius: 'var(--r-sm)',
                    border: `1px solid ${isTdy ? 'var(--gold)' : 'var(--border-soft)'}`,
                    background: inMonth ? 'rgba(0,8,24,0.4)' : 'rgba(0,8,24,0.15)',
                    opacity: inMonth ? 1 : 0.5,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--f-heading)',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: isTdy ? 'var(--gold)' : 'var(--text)',
                      }}
                    >
                      {Number(dIso.slice(8, 10))}
                    </span>
                    {rCount > 0 && (
                      <span style={{ fontSize: '8px', color: 'var(--text-mute)' }}>▨{rCount}</span>
                    )}
                  </div>
                  {daySessions.slice(0, 3).map((d) => {
                    const col = sessionColor(d.session, isTdy, nowMin);
                    return (
                      <span
                        key={d.session.id}
                        style={{
                          fontSize: '9.5px',
                          color: col.text,
                          background: col.bg,
                          borderLeft: `2px solid ${col.border}`,
                          borderRadius: '2px',
                          padding: '1px 4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.35,
                        }}
                      >
                        {fmtHM(d.startMin)} {d.content?.title || '—'}
                      </span>
                    );
                  })}
                  {daySessions.length > 3 && (
                    <span
                      style={{
                        fontFamily: 'var(--f-mono)',
                        fontSize: '9px',
                        color: 'var(--purple)',
                      }}
                    >
                      +{daySessions.length - 3} · {done}✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* legenda */}
      <div
        style={{
          display: 'flex',
          gap: '14px',
          flexWrap: 'wrap',
          marginTop: '12px',
          fontFamily: 'var(--f-mono)',
          fontSize: '10px',
          color: 'var(--text-dim)',
        }}
      >
        <span style={{ color: 'var(--cyan)' }}>■ estudo planejado</span>
        <span style={{ color: 'var(--gold)' }}>■ em andamento</span>
        <span style={{ color: 'var(--green)' }}>■ feita</span>
        <span style={{ color: 'var(--amber)' }}>■ atrasada</span>
        <span>▨ rotina (clique p/ editar)</span>
        <span style={{ color: 'var(--pink)' }}>— agora</span>
      </div>

      <RoutineModal
        open={routineModal.open}
        routine={routineModal.routine}
        onClose={() => setRoutineModal({ open: false, routine: null })}
      />
    </div>
  );
}
